import { IonAlert, IonButton } from '@ionic/react';
import {
  clearPendingCropPictures,
  deletePendingCropPicture,
  UploadPictureDto,
  uploadCropPictureOptions,
} from '@lidea/shared/data-access/analysis';
import {
  clearAllMutations,
  clearMutation,
} from '@lidea/shared/data-access/core';
import { hashKey, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import classes from './clear-mutations-button.module.css';

interface ClearMutationsButtonProps {
  id?: number;
  children?: React.ReactNode;
}

export const ClearMutationsButton: React.FC<ClearMutationsButtonProps> = ({
  id,
  children,
}) => {
  const { t } = useTranslation(['common'], { useSuspense: false });
  const queryClient = useQueryClient();

  const clearPendingPictureForMutation = async (mutationId: number) => {
    const mutation = queryClient.getMutationCache().find({
      predicate: (m) => m.mutationId === mutationId,
    });
    if (
      !mutation?.options.mutationKey ||
      hashKey(mutation.options.mutationKey) !==
        hashKey(uploadCropPictureOptions.mutationKey)
    ) {
      return;
    }
    const pictureId = (mutation.state.variables as UploadPictureDto | undefined)
      ?.pictureId;
    if (pictureId) {
      await deletePendingCropPicture(pictureId);
    }
  };

  return (
    <>
      <IonButton
        color="danger"
        id={`clear-mutations-button-${id ?? 'all'}`}
        size="small"
      >
        {children}
      </IonButton>
      <IonAlert
        buttons={[
          {
            text: t('common:cancel'),
            role: 'cancel',
            cssClass: classes.cancelButton,
          },
          {
            text: t('common:confirm'),
            role: 'confirm',
            cssClass: classes.confirmButton,
          },
        ]}
        header={
          id
            ? t('common:clear_mutation_header')!
            : t('common:clear_mutations_header')!
        }
        message={t('common:irreversible_action')!}
        trigger={`clear-mutations-button-${id ?? 'all'}`}
        onDidDismiss={async ({ detail }) => {
          if (detail.role !== 'confirm') {
            return;
          }
          if (id) {
            await clearPendingPictureForMutation(id);
            clearMutation(queryClient, id);
            return;
          }
          await clearPendingCropPictures();
          clearAllMutations(queryClient);
        }}
      />
    </>
  );
};
